import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from './Button';
import {
  IconPencil,
  IconX,
  IconSearch,
  IconChevronRight,
  IconChevronDown,
} from '@tabler/icons-react';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Sig auto-generation — mirrors backend (app/schemas/medications.py) for
// instant preview. Provider-facing format:
//   [drug] [strength], [qty] [unit(s)] [route?] [frequency] [PRN?]
// ---------------------------------------------------------------------------

const FREQUENCY_PHRASE = {
  'Daily': 'daily',
  'Twice daily': 'twice daily',
  'Three times daily': 'three times daily',
  'Four times daily': 'four times daily',
  'Every morning': 'every morning',
  'Every evening': 'every evening',
  'At bedtime': 'at bedtime',
  'Every other day': 'every other day',
  'Every Monday/Wednesday/Friday': 'every Monday, Wednesday, and Friday',
  'Every Tuesday/Thursday/Saturday': 'every Tuesday, Thursday, and Saturday',
  'Twice weekly': 'twice weekly',
  'Three times weekly': 'three times weekly',
  'Weekly': 'weekly',
  'Every 2 weeks': 'every 2 weeks',
  'Every 3 weeks': 'every 3 weeks',
  'Every 4 weeks': 'every 4 weeks',
  'Monthly': 'monthly',
  'Every 3 months': 'every 3 months',
  'Every 4 hours': 'every 4 hours',
  'Every 6 hours': 'every 6 hours',
  'Every 8 hours': 'every 8 hours',
  'Every 12 hours': 'every 12 hours',
  'Before meals': 'before meals',
  'With meals': 'with meals',
  'After meals': 'after meals',
  'Before meals and at bedtime': 'before meals and at bedtime',
  'As needed': 'as needed',
  'Once (single dose)': 'once',
};

const ROUTE_TOKEN = {
  'Oral': 'PO',
  'Subcutaneous': 'SQ',
  'Intravenous': 'IV',
  'Inhaled': 'inhaled',
  'Topical': 'topical',
  'Intranasal': 'intranasal',
  'Sublingual': 'SL',
  'Transdermal': 'transdermal',
  'Ophthalmic': 'ophthalmic',
  'Otic': 'otic',
  'Rectal': 'PR',
  'Intramuscular': 'IM',
  'Intravitreal': 'intravitreal',
};

const DOSE_UNIT_PLURAL = { patch: 'patches', suppository: 'suppositories', spray: 'sprays' };
const DOSE_UNITS_NO_PLURAL = new Set(['mL', 'unit', 'mg', 'mcg']);

function pluralizeUnit(unit, qty) {
  if (!unit) return unit;
  const n = parseFloat(qty);
  if (!qty || n === 1) return unit;
  if (DOSE_UNITS_NO_PLURAL.has(unit)) return unit;
  if (DOSE_UNIT_PLURAL[unit]) return DOSE_UNIT_PLURAL[unit];
  return unit + 's';
}

function fmtQty(qty) {
  if (qty === '' || qty == null) return null;
  const n = parseFloat(qty);
  if (Number.isNaN(n)) return null;
  return String(n);
}

// Derive a default dose unit from a medication name: 'tablet' or 'capsule'
// when the name clearly contains one (whole-word, case-insensitive), else ''
// (leave the picker untouched — current behavior for injections, solutions,
// and names without a form).
function deriveDoseUnit(name) {
  const s = (name || '').toLowerCase();
  if (/\btablets?\b/.test(s)) return 'tablet';
  if (/\bcapsules?\b/.test(s)) return 'capsule';
  return '';
}

function generateSig({ drug_name, strength_form, quantity_per_dose, dose_units, route, frequency, prn, prn_reason }) {
  if (!frequency || frequency === 'Other') return '';

  const prefixParts = [];
  if (drug_name && drug_name.trim()) prefixParts.push(drug_name.trim());
  if (strength_form && strength_form.trim()) prefixParts.push(strength_form.trim());
  const prefix = prefixParts.join(' ');

  const bodyParts = [];
  const q = fmtQty(quantity_per_dose);
  if (q && dose_units) bodyParts.push(`${q} ${pluralizeUnit(dose_units, quantity_per_dose)}`);
  else if (q) bodyParts.push(q);

  // Route token. Blank route means oral by convention, so it gets PO too.
  if (route && ROUTE_TOKEN[route]) bodyParts.push(ROUTE_TOKEN[route]);
  else if (!route) bodyParts.push('PO');
  bodyParts.push(FREQUENCY_PHRASE[frequency] || frequency.toLowerCase());

  if (prn && frequency !== 'As needed') {
    bodyParts.push(prn_reason?.trim() ? `PRN ${prn_reason.trim()}` : 'PRN');
  }

  const body = bodyParts.join(' ');
  if (prefix && body) return `${prefix}, ${body}`;
  return prefix || body || '';
}

// ---------------------------------------------------------------------------
// Controlled-list fallbacks (loaded from API on mount)
// ---------------------------------------------------------------------------

const DOSE_UNITS_DEFAULT = [
  'tablet', 'capsule', 'mL', 'unit', 'dose', 'puff', 'spray',
  'patch', 'application', 'drop', 'suppository', 'lozenge', 'packet',
];
const ROUTES_DEFAULT = [
  'Oral', 'Subcutaneous', 'Intravenous', 'Inhaled', 'Topical',
  'Intranasal', 'Sublingual', 'Transdermal', 'Ophthalmic', 'Otic',
  'Rectal', 'Intramuscular', 'Intravitreal',
];
const FREQUENCIES_DEFAULT = [
  'Daily', 'Twice daily', 'Three times daily', 'Four times daily',
  'Every morning', 'Every evening', 'At bedtime',
  'Every other day', 'Every Monday/Wednesday/Friday',
  'Every Tuesday/Thursday/Saturday', 'Twice weekly', 'Three times weekly',
  'Weekly', 'Every 2 weeks', 'Every 3 weeks', 'Every 4 weeks', 'Monthly',
  'Every 3 months', 'Every 4 hours', 'Every 6 hours', 'Every 8 hours',
  'Every 12 hours', 'Before meals', 'With meals', 'After meals',
  'Before meals and at bedtime', 'As needed', 'Other', 'Once (single dose)',
];
const DEACTIVATION_REASONS_DEFAULT = [
  'Discontinued', 'Replaced', 'Entered in error', 'EMR integration',
];

// Dose quantity presets (editable — users may type other values).
const DOSE_PRESETS = ['0.5', '1', '1.5', '2', '2.5', '3', '4', '5'];

// Frequency shorthand aliases → canonical frequency value. Matching is
// case-insensitive and ignores periods/spaces (so "b.i.d." === "bid").
const FREQUENCY_ALIASES = {
  daily: 'Daily', qd: 'Daily', od: 'Daily', qday: 'Daily',
  oncedaily: 'Daily', onceaday: 'Daily', everyday: 'Daily', '1xday': 'Daily',
  bid: 'Twice daily', twiceaday: 'Twice daily', '2xday': 'Twice daily', twotimesaday: 'Twice daily',
  tid: 'Three times daily', '3xday': 'Three times daily', threetimesaday: 'Three times daily',
  qid: 'Four times daily', '4xday': 'Four times daily', fourtimesaday: 'Four times daily',
  qod: 'Every other day', qad: 'Every other day', alternatedays: 'Every other day',
  qam: 'Every morning',
  qpm: 'Every evening',
  qhs: 'At bedtime', hs: 'At bedtime', bedtime: 'At bedtime',
  q4h: 'Every 4 hours', q4: 'Every 4 hours',
  q6h: 'Every 6 hours', q6: 'Every 6 hours',
  q8h: 'Every 8 hours', q8: 'Every 8 hours',
  q12h: 'Every 12 hours', q12: 'Every 12 hours',
  biw: 'Twice weekly',
  tiw: 'Three times weekly',
  qweek: 'Weekly', qw: 'Weekly', onceaweek: 'Weekly', everyweek: 'Weekly',
  qmwf: 'Every Monday/Wednesday/Friday', mwf: 'Every Monday/Wednesday/Friday',
  qtts: 'Every Tuesday/Thursday/Saturday', tts: 'Every Tuesday/Thursday/Saturday',
  qths: 'Every Tuesday/Thursday/Saturday', ths: 'Every Tuesday/Thursday/Saturday',
  ac: 'Before meals', beforemeals: 'Before meals',
  cc: 'With meals', withmeals: 'With meals', withfood: 'With meals',
  pc: 'After meals', aftermeals: 'After meals',
  prn: 'As needed', asneeded: 'As needed',
  once: 'Once (single dose)', x1: 'Once (single dose)', singledose: 'Once (single dose)', onetime: 'Once (single dose)',
};

// Normalize a typed token for alias lookup: lowercase, strip periods/spaces/slashes.
function normalizeFreqToken(s) {
  return (s || '').toLowerCase().replace(/[.\s/]/g, '');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isEditable(record) {
  return record?.source === 'manual';
}

function fmtAuditDate(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleDateString('en-US', {
    month: '2-digit', day: '2-digit', year: '2-digit',
  });
}

const EMPTY_FORM = {
  catalog_id: null,
  drug_name: '', rxnorm_cui: '', rxnorm_cui_ingredient: '', strength_form: '',
  quantity_per_dose: '', dose_units: '', route: '', frequency: '',
  prn: false, prn_reason: '', sig: '', additional_info: '',
  highlight_in_list: false,
  free_text_entry: false, from_catalog: false,
  // Unified strength-sibling list: [{ key, label, rxnorm_cui }]
  siblings: [],
};

// ---------------------------------------------------------------------------
// ComboBox — editable dropdown (type a value OR pick from the list).
// Used for dose presets and frequency. For frequency, an optional
// aliasResolver maps typed shorthand (e.g. "bid") to a canonical value.
// ---------------------------------------------------------------------------
function ComboBox({ value, options, onChange, placeholder, aliasResolver, style, inputRef }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(value || '');
  const [isFiltering, setIsFiltering] = useState(false); // typing narrows; browsing shows all
  const [highlight, setHighlight] = useState(-1);         // index into navItems; -1 = none
  const wrapRef = useRef(null);
  const localRef = useRef(null);
  const menuRef = useRef(null);
  const fieldRef = inputRef || localRef;

  useEffect(() => { setTyped(value || ''); }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = (typed || '').toLowerCase().trim();
  const aliasHit = aliasResolver ? aliasResolver(typed) : null;
  const showAlias = !!(aliasHit && aliasHit !== typed);

  // Filter only while actively typing. When the box is opened to browse (focus
  // or chevron click) the committed value should NOT filter the list to itself
  // (or to nothing, for numeric presets) — show the full list so the user can
  // pick a different option without first clearing the field.
  const filtered = (!isFiltering || !q)
    ? options
    : options.filter(o =>
        o.toLowerCase().includes(q) || (aliasHit && o === aliasHit));

  // Unified navigable list: the alias hint (when shown) is the first row, then
  // the filtered options. Arrow keys and Enter operate over this.
  const navItems = (showAlias ? [aliasHit] : []).concat(filtered);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || highlight < 0 || !menuRef.current) return;
    const row = menuRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // Reset the highlight whenever the menu opens or the navigable set changes.
  // Start with nothing highlighted so Enter (with no arrow navigation yet) can
  // bubble to enter-to-save; the highlight engages only once the user arrows.
  useEffect(() => {
    setHighlight(-1);
  }, [open, typed]);

  const commit = (val) => {
    onChange(val);
    setTyped(val);
    setIsFiltering(false);
    setOpen(false);
    setHighlight(-1);
  };

  const onBlurResolve = () => {
    if (aliasResolver) {
      const hit = aliasResolver(typed);
      if (hit) { commit(hit); return; }
    }
    onChange(typed);
    setIsFiltering(false);
  };

  const toggleOpen = () => {
    if (open) { setOpen(false); return; }
    setIsFiltering(false);   // opening via chevron = browse, show all
    setOpen(true);
    if (fieldRef.current) fieldRef.current.focus();
  };

  const move = (delta) => {
    if (!open) { setIsFiltering(false); setOpen(true); return; }
    if (navItems.length === 0) return;
    setHighlight(h => {
      const next = h < 0 ? (delta > 0 ? 0 : navItems.length - 1) : h + delta;
      return Math.max(0, Math.min(navItems.length - 1, next));
    });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <input
        ref={fieldRef}
        style={comboStyles.input}
        value={typed}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        onChange={e => { setTyped(e.target.value); setIsFiltering(true); setOpen(true); }}
        onFocus={() => { setIsFiltering(false); setOpen(true); }}
        onBlur={onBlurResolve}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
          else if (e.key === 'Enter') {
            if (open && highlight >= 0 && navItems[highlight] != null) {
              e.preventDefault(); commit(navItems[highlight]);
            } else if (aliasHit) { e.preventDefault(); commit(aliasHit); }
            else if (isFiltering && filtered.length === 1) { e.preventDefault(); commit(filtered[0]); }
            else if (open) { e.preventDefault(); setOpen(false); }
            // else: nothing to commit and menu closed -> let Enter bubble
            // (drawer-level enter-to-save handles it).
          } else if (e.key === 'Escape') { setOpen(false); }
          else if (e.key === 'Tab') {
            // Commit the highlighted item (so arrow-then-tab keeps the choice),
            // then let focus advance to the next field.
            if (open && highlight >= 0 && navItems[highlight] != null) {
              commit(navItems[highlight]);
            } else {
              setOpen(false);
            }
          }
        }}
      />
      <button
        type="button"
        style={comboStyles.caretBtn}
        onMouseDown={e => { e.preventDefault(); toggleOpen(); }}
        tabIndex={-1}
        aria-label="Toggle options"
      >
        <IconChevronDown size={15} stroke={1.5} style={{ color: 'var(--color-text-primary)' }} />
      </button>
      {open && navItems.length > 0 && (
        <div ref={menuRef} style={comboStyles.menu}>
          {showAlias && (
            <div
              data-idx="0"
              style={{
                ...comboStyles.aliasHint,
                ...(highlight === 0 ? comboStyles.optionHighlight : {}),
              }}
              onMouseEnter={() => setHighlight(0)}
              onMouseDown={e => { e.preventDefault(); commit(aliasHit); }}
            >
              ↵ {aliasHit} <span style={comboStyles.aliasNote}>(from "{typed}")</span>
            </div>
          )}
          {filtered.map((o, i) => {
            const idx = showAlias ? i + 1 : i;
            return (
              <div
                key={o}
                data-idx={idx}
                style={{
                  ...comboStyles.option,
                  ...(o === value ? comboStyles.optionActive : {}),
                  ...(highlight === idx ? comboStyles.optionHighlight : {}),
                }}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={e => { e.preventDefault(); commit(o); }}
              >
                {o}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const comboStyles = {
  input: {
    width: '100%', fontSize: '13px', padding: '7px 28px 7px 10px',
    border: '1px solid var(--color-border)', borderRadius: '4px',
    fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-card)', boxSizing: 'border-box',
  },
  caretBtn: {
    position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '22px', height: '22px', padding: 0,
    border: 'none', background: 'transparent', cursor: 'pointer',
  },
  menu: {
    position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 30,
    backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
    borderRadius: '4px', boxShadow: 'var(--shadow-md)',
    maxHeight: '220px', overflowY: 'auto',
  },
  option: {
    padding: '7px 10px', fontSize: '13px', cursor: 'pointer',
    color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)',
    backgroundColor: 'var(--color-bg-card)',
  },
  optionActive: { backgroundColor: 'var(--color-accent-wash)', fontWeight: 600 },
  optionHighlight: { backgroundColor: 'var(--color-accent-tint)' },
  aliasHint: {
    padding: '7px 10px', fontSize: '13px', cursor: 'pointer',
    color: 'var(--color-primary)', fontWeight: 600, fontFamily: 'var(--font-family-base)',
    borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-accent-wash)',
  },
  aliasNote: { fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '11px' },
};

// ===========================================================================
// Search modal — unified type-ahead: catalog (strength rows) + RxNorm (names)
//
// Catalog results are strength-level and pick straight through to the drawer.
// In the NESTED catalog model an ingredient expands to flat strength rows:
// the ingredient's generic SCD strengths PLUS each brand child's SBD strengths
// (Option A — flat, brand-labeled). Each flat row knows its OWNER (the generic
// ingredient, or a specific brand), and carries that owner's full strength set
// so the drawer's strength-switcher offers only same-owner siblings (the
// drill-to-child: pick a Lantus strength -> Lantus siblings, not Toujeo's).
//
// RxNorm results are NAMES (from the cached index) that expand to a strength
// list on click (accordion); picking a strength goes to the drawer. Catalog
// fires at 2+ chars; RxNorm names at 3+ (the index minimum).
// ===========================================================================

function MedicationSearchModal({ onPick, onClose }) {
  const [query, setQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]); // expanded flat strength rows
  const [rxnormNames, setRxnormNames] = useState([]);       // name strings from the index
  const [searchingCatalog, setSearchingCatalog] = useState(false);
  const [searchingNames, setSearchingNames] = useState(false);
  // Accordion: which RxNorm name is expanded, and its fetched strength rows.
  const [expandedName, setExpandedName] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);     // RxNormDrugResult rows
  const [loadingStrengths, setLoadingStrengths] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Expand each catalog ingredient into flat strength rows: the generic SCD
  // forms (owner='generic') plus each brand child's SBD forms (owner='brand').
  // A drug with no forms anywhere yields a single drug-level row. Each row
  // carries siblingForms = the full form list of its OWNER, so the drawer's
  // strength switcher is scoped to the same owner (drill-to-child).
  const expandCatalog = useCallback(async (entries) => {
    const rows = [];
    for (const e of entries) {
      const ingredientCtx = {
        catalog_id: e.id,
        drug_generic: e.generic_name,
        rxnorm_cui_ingredient: e.rxnorm_cui_ingredient || '',
        highlight_in_list: e.highlight_in_list,
      };

      let anyForms = false;

      // ── Generic (parent) strengths ──
      if (e.has_forms) {
        try {
          const res = await api.get(`/reference/medications/catalog/${e.id}/forms`);
          const forms = res.data || [];
          forms.forEach(f => {
            rows.push({
              ...ingredientCtx,
              kind: 'catalog',
              key: `c${e.id}-g-f${f.id}`,
              owner: 'generic',
              brand_name: '',
              label: f.display_label,            // generic SCD label
              rxnorm_cui: f.rxnorm_cui || '',
              siblingForms: forms,               // same-owner siblings
            });
          });
          if (forms.length) anyForms = true;
        } catch { /* ignore generic form fetch failure */ }
      }

      // ── Brand (child) strengths ──
      for (const b of (e.brands || [])) {
        if (!b.has_forms) continue;
        try {
          const res = await api.get(`/reference/medications/catalog/brands/${b.id}/forms`);
          const forms = res.data || [];
          forms.forEach(f => {
            rows.push({
              ...ingredientCtx,
              kind: 'catalog',
              key: `c${e.id}-b${b.id}-f${f.id}`,
              owner: 'brand',
              brand_id: b.id,
              brand_name: b.brand_name,
              label: f.display_label,            // SBD label (brand-bearing)
              rxnorm_cui: f.rxnorm_cui || '',
              siblingForms: forms,               // same-brand siblings
            });
          });
          if (forms.length) anyForms = true;
        } catch { /* ignore brand form fetch failure */ }
      }

      // ── Drug-level fallback (no forms anywhere) ──
      if (!anyForms) {
        rows.push({
          ...ingredientCtx,
          kind: 'catalog',
          key: `c${e.id}-drug`,
          owner: 'generic',
          brand_name: '',
          label: e.generic_name,
          rxnorm_cui: '',
          siblingForms: [],
        });
      }
    }
    return rows;
  }, []);

  // Run both lookups for a query. Catalog at 2+, RxNorm names at 3+.
  const runSearch = useCallback(async (q) => {
    const term = q.trim();
    setExpandedName(null);
    setExpandedRows([]);

    if (term.length >= 2) {
      setSearchingCatalog(true);
      try {
        const res = await api.get(`/reference/medications/catalog?q=${encodeURIComponent(term)}`);
        setCatalogResults(await expandCatalog(res.data || []));
      } catch {
        setCatalogResults([]);
      } finally {
        setSearchingCatalog(false);
      }
    } else {
      setCatalogResults([]);
    }

    if (term.length >= 3) {
      setSearchingNames(true);
      try {
        const res = await api.get(`/reference/medications/rxnorm/names?q=${encodeURIComponent(term)}`);
        setRxnormNames(res.data || []);
      } catch {
        setRxnormNames([]);
      } finally {
        setSearchingNames(false);
      }
    } else {
      setRxnormNames([]);
    }
  }, [expandCatalog]);

  const handleChange = (value) => {
    setQuery(value);
    if (timer.current) clearTimeout(timer.current);
    if (value.trim().length < 2) {
      setCatalogResults([]); setRxnormNames([]);
      setExpandedName(null); setExpandedRows([]);
      return;
    }
    timer.current = setTimeout(() => runSearch(value), 300);
  };

  // Expand a RxNorm name → fetch its strength×form rows (brand- or generic-
  // branched on the backend). Toggle closed if already open.
  const toggleName = async (name) => {
    if (expandedName === name) {
      setExpandedName(null);
      setExpandedRows([]);
      return;
    }
    setExpandedName(name);
    setExpandedRows([]);
    setLoadingStrengths(true);
    try {
      const res = await api.get(`/reference/medications/rxnorm?q=${encodeURIComponent(name)}`);
      setExpandedRows(res.data || []);
    } catch {
      setExpandedRows([]);
    } finally {
      setLoadingStrengths(false);
    }
  };

  // Derive strength siblings for a picked RxNorm row, from the expanded set:
  // same ingredient_key + dose_form + branded/generic identity.
  const rxnormSiblings = (picked) => {
    const sibs = expandedRows.filter(r =>
      r.ingredient_key === picked.ingredient_key &&
      r.dose_form === picked.dose_form &&
      r.is_branded === picked.is_branded
    );
    const mapped = sibs.map(r => ({
      key: r.rxnorm_cui, label: r.display_label, rxnorm_cui: r.rxnorm_cui,
    }));
    mapped.sort((a, b) => (a.rxnorm_cui === picked.rxnorm_cui ? -1
      : b.rxnorm_cui === picked.rxnorm_cui ? 1 : 0));
    return mapped;
  };

  // Pick a flat catalog strength row. Siblings come from the row's OWNER form
  // set (generic forms for a generic row, that brand's forms for a brand row)
  // — already attached at expand time, so no extra fetch. The full strength
  // label is the drug_name; strength_form stays empty (consistent storage).
  const pickCatalog = (row) => {
    api.post(`/reference/medications/catalog/${row.catalog_id}/select`).catch(() => {});
    const siblings = (row.siblingForms || []).map(f => ({
      key: f.rxnorm_cui ? `cui:${f.rxnorm_cui}` : `lbl:${f.display_label}`,
      label: f.display_label,
      rxnorm_cui: f.rxnorm_cui || '',
    }));
    onPick({
      catalog_id: row.catalog_id, from_catalog: true,
      drug_name: row.label,
      strength_form: '',
      rxnorm_cui: row.rxnorm_cui || '',
      rxnorm_cui_ingredient: row.rxnorm_cui_ingredient || '',
      ingredient_name: '',
      highlight_in_list: row.highlight_in_list || false,
      siblings,
    });
  };

  const pickRxnorm = (row) => {
    onPick({
      catalog_id: null, from_catalog: false,
      drug_name: row.display_label, strength_form: '',
      rxnorm_cui: row.rxnorm_cui || '', rxnorm_cui_ingredient: '',
      ingredient_name: row.ingredient_key || '',
      highlight_in_list: false,
      siblings: rxnormSiblings(row),
    });
  };

  const renderCatalogRow = (row) => {
    // Brand rows: the SBD label already carries the brand (e.g. "Lantus 100
    // unit/mL ..."), so show it directly; the generic ingredient is the
    // sub-line context. Generic rows: the SCD label, no sub-line.
    const sub = row.owner === 'brand'
      ? row.drug_generic
      : '';
    return (
      <div key={row.key} style={ms.resultRow} onMouseDown={() => pickCatalog(row)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{
            ...ms.resultName,
            color: row.highlight_in_list ? 'var(--color-primary)' : 'var(--color-text-primary)',
          }}>
            {row.label}
          </span>
          {sub && <span style={ms.resultSub}>{sub}</span>}
        </div>
      </div>
    );
  };

  // A RxNorm name row + (when expanded) its strength accordion.
  const renderRxnormName = (name) => {
    const open = expandedName === name;
    const branded = expandedRows.filter(r => r.is_branded);
    const generic = expandedRows.filter(r => !r.is_branded);
    return (
      <div key={name}>
        <div
          style={{ ...ms.nameRow, ...(open ? ms.nameRowOpen : {}) }}
          onMouseDown={(e) => { e.preventDefault(); toggleName(name); }}
        >
          {open
            ? <IconChevronDown size={15} stroke={1.5} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
            : <IconChevronRight size={15} stroke={1.5} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />}
          <span style={ms.nameText}>{name}</span>
        </div>
        {open && (
          <div style={ms.accordion}>
            {loadingStrengths ? (
              <div style={ms.accordionHint}>Loading strengths…</div>
            ) : expandedRows.length === 0 ? (
              <div style={ms.accordionHint}>No strengths found.</div>
            ) : (
              <>
                {branded.map(r => (
                  <div key={r.rxnorm_cui} style={ms.strengthRow} onMouseDown={() => pickRxnorm(r)}>
                    {r.display_label}
                  </div>
                ))}
                {branded.length > 0 && generic.length > 0 && <div style={ms.rxDivider} />}
                {generic.map(r => (
                  <div key={r.rxnorm_cui} style={ms.strengthRow} onMouseDown={() => pickRxnorm(r)}>
                    {r.display_label}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const queryReady = query.trim().length >= 2;
  const hasCatalog = catalogResults.length > 0;
  const hasNames = rxnormNames.length > 0;

  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <div style={ms.header}>
          <h2 style={ms.title}>Add Medication</h2>
          <button onClick={onClose} style={ms.closeBtn} aria-label="Close">
            <IconX size={18} stroke={2} />
          </button>
        </div>

        <div className="nephros-input-wrap" style={ms.searchBar}>
          <IconSearch size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            style={ms.searchInput}
            value={query}
            onChange={e => handleChange(e.target.value)}
            placeholder="Search drug or brand name..."
            autoComplete="off"
          />
          {(searchingCatalog || searchingNames) && (
            <span style={ms.searchingText}>Searching...</span>
          )}
        </div>

        <div style={ms.results}>
          {!queryReady && (
            <div style={ms.hint}>Type at least 2 characters to search.</div>
          )}

          {queryReady && hasCatalog && (
            <>
              <div style={ms.sectionLabel}>Catalog</div>
              {catalogResults.map(renderCatalogRow)}
            </>
          )}

          {queryReady && hasNames && (
            <>
              <div style={ms.sectionLabel}>RxNorm</div>
              {rxnormNames.map(renderRxnormName)}
            </>
          )}

          {queryReady && !hasCatalog && !hasNames && !searchingCatalog && !searchingNames && (
            <div style={ms.hint}>
              No matches for &ldquo;{query.trim()}&rdquo;.
              {query.trim().length < 3 && ' Type one more character to search RxNorm.'}
            </div>
          )}
        </div>

        <div style={ms.footer}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}


// ===========================================================================
// MedicationsSection — main
// ===========================================================================

export default function MedicationsSection({ patient, resolveUser }) {
  const patientId = patient.id;

  // Data
  const [medications, setMedications] = useState([]);
  const [lists, setLists] = useState({
    dose_units: DOSE_UNITS_DEFAULT,
    routes: ROUTES_DEFAULT,
    frequencies: FREQUENCIES_DEFAULT,
    deactivation_reasons: DEACTIVATION_REASONS_DEFAULT,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('active');
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const doseRef = useRef(null);   // focus target after a med is picked

  // Anemia management orders — populated by the Anemia module in a later
  // stage. Empty in Phase 1, so the contextual zone stays hidden.
  const anemiaOrders = [];

  // Drawer form
  const [form, setForm] = useState(EMPTY_FORM);
  const [sigOverridden, setSigOverridden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmDisc, setConfirmDisc] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [discReason, setDiscReason] = useState('discontinued');

  // ── Load ──
  const loadMedications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/patients/${patientId}/medications`);
      setMedications(res.data);
    } catch {
      setError('Failed to load medications.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadMedications();
    api.get('/reference/medications/lists')
      .then(r => setLists(r.data))
      .catch(() => {});
  }, [loadMedications]);

  // ── Auto-sig ──
  useEffect(() => {
    if (sigOverridden || form.frequency === 'Other') return;
    setForm(f => ({ ...f, sig: generateSig(f) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.drug_name, form.strength_form, form.quantity_per_dose,
    form.dose_units, form.route, form.frequency, form.prn, form.prn_reason,
  ]);

  // ── Drawer open from a search pick ──
  // Siblings now arrive ON the pick (owner-scoped), so there is no post-open
  // fetch — picking a Lantus strength already carries Lantus's siblings.
  const openFromPick = (pick) => {
    setEditingMed(null);
    setForm({
      ...EMPTY_FORM,
      catalog_id: pick.catalog_id,
      from_catalog: pick.from_catalog,
      drug_name: pick.drug_name,
      strength_form: pick.strength_form,
      rxnorm_cui: pick.rxnorm_cui,
      rxnorm_cui_ingredient: pick.rxnorm_cui_ingredient,
      highlight_in_list: pick.highlight_in_list,
      dose_units: deriveDoseUnit(pick.drug_name),  // tablet/capsule if in name
      siblings: pick.siblings || [],
    });
    setSigOverridden(false);
    setFormError(null);
    setConfirmDisc(false);
    setConfirmDel(false);
    setSearchOpen(false);
    setDrawerOpen(true);
    // Land the cursor in Dose so the user can start dosing immediately.
    setTimeout(() => { if (doseRef.current) doseRef.current.focus(); }, 60);
  };

  const openEdit = (med) => {
    if (!isEditable(med)) return;
    setEditingMed(med);
    setForm({
      ...EMPTY_FORM,
      drug_name: med.drug_name || '',
      rxnorm_cui: med.rxnorm_cui || '',
      strength_form: med.strength_form || '',
      quantity_per_dose: med.quantity_per_dose != null ? String(parseFloat(med.quantity_per_dose)) : '',
      dose_units: med.dose_units || '',
      route: med.route || '',
      frequency: med.frequency || '',
      prn: med.prn || false,
      prn_reason: med.prn_reason || '',
      sig: med.sig || '',
      additional_info: med.additional_info || '',
      highlight_in_list: med.highlight_in_list || false,
      free_text_entry: med.free_text_entry || false,
      from_catalog: false,
      siblings: [],
    });
    // Show stored sig as-is on open; touching any dosing field regenerates it.
    setSigOverridden(true);
    setFormError(null);
    setConfirmDisc(false);
    setConfirmDel(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingMed(null);
    setForm(EMPTY_FORM);
    setSigOverridden(false);
    setFormError(null);
    setConfirmDisc(false);
    setConfirmDel(false);
  };

  const setField = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  // Changing any sig-affecting field clears a manual/stored-sig override.
  const setDoseField = (field, value) => {
    setSigOverridden(false);
    setForm(f => ({ ...f, [field]: value }));
  };

  // Switch to a different strength (sibling). Works for both catalog and
  // RxNorm drugs — siblings carry { key, label, rxnorm_cui }.
  const selectSibling = (key) => {
    const sib = form.siblings.find(s => String(s.key) === String(key));
    if (!sib) return;
    setSigOverridden(false);
    setForm(f => {
      // Re-derive tablet/capsule from the new strength label; if the new form
      // isn't clearly tablet/capsule (e.g. an injection), keep the existing
      // unit rather than clobbering a deliberate choice.
      const derived = deriveDoseUnit(sib.label);
      return {
        ...f,
        drug_name: sib.label,
        strength_form: '',
        rxnorm_cui: sib.rxnorm_cui || '',
        dose_units: derived || f.dose_units,
      };
    });
  };

  // ── Save / deactivate / delete ──
  // Enter-to-save, scoped to simple inputs. Skips textareas (Enter = newline
  // there), skips events a ComboBox already handled (it preventDefaults its own
  // Enter for alias/option commit), and only fires when not mid-save.
  const onDrawerKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (e.defaultPrevented) return;                       // ComboBox handled it
    const tag = (e.target.tagName || '').toUpperCase();
    if (tag === 'TEXTAREA') return;                        // newline in Instructions/Additional
    if (saving || deleting) return;
    e.preventDefault();
    handleSave();
  };

  const handleSave = async () => {
    if (!form.drug_name.trim()) { setFormError('Medication name is required.'); return; }
    setSaving(true);
    setFormError(null);
    const payload = {
      catalog_id: form.catalog_id || null,
      drug_name: form.drug_name.trim(),
      rxnorm_cui: form.rxnorm_cui || null,
      strength_form: form.strength_form || null,
      quantity_per_dose: form.quantity_per_dose ? parseFloat(form.quantity_per_dose) : null,
      dose_units: form.dose_units || null,
      route: form.route || null,
      frequency: form.frequency || null,
      prn: form.prn,
      prn_reason: form.prn ? (form.prn_reason || null) : null,
      sig: form.sig || null,
      additional_info: form.additional_info || null,
      highlight_in_list: form.highlight_in_list,
      free_text_entry: form.free_text_entry,
    };
    try {
      if (editingMed) {
        await api.put(`/patients/${patientId}/medications/${editingMed.id}`, payload);
      } else {
        await api.post(`/patients/${patientId}/medications`, payload);
      }
      await loadMedications();
      closeDrawer();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!editingMed) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.patch(`/patients/${patientId}/medications/${editingMed.id}/deactivate`,
        { deactivation_reason: discReason });
      await loadMedications();
      closeDrawer();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Discontinue failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingMed) return;
    setDeleting(true);
    setFormError(null);
    try {
      await api.delete(`/patients/${patientId}/medications/${editingMed.id}`);
      await loadMedications();
      closeDrawer();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived ──
  const activeMeds = medications.filter(m => m.is_active);
  const inactiveMeds = medications.filter(m => !m.is_active);
  const displayMeds = activeTab === 'active' ? activeMeds : inactiveMeds;

  const editingRecord = editingMed;

  // ── Audit line ──
  const auditLine = editingRecord
    ? (editingRecord.modified_by && editingRecord.modified_by !== editingRecord.created_by
      ? `Last edited by ${resolveUser(editingRecord.modified_by)} on ${fmtAuditDate(editingRecord.updated_at)}`
      : `Added by ${resolveUser(editingRecord.created_by)} on ${fmtAuditDate(editingRecord.created_at)}`)
    : null;

  // ---------------------------------------------------------------------------
  // Drawer (mirrors _SectionTemplate.js)
  // ---------------------------------------------------------------------------
  const renderDrawer = () => {
    const hasSiblings = form.siblings && form.siblings.length > 1;
    const sourced = form.from_catalog || !!form.rxnorm_cui_ingredient || !!form.rxnorm_cui;
    return (
      <div style={{
        width: drawerOpen ? '480px' : '0px',
        opacity: drawerOpen ? 1 : 0,
        overflow: 'hidden',
        transition: 'width 0.25s ease, opacity 0.2s ease',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderTop: '3px solid var(--color-accent-navy)',
        borderRight: drawerOpen ? '1px solid var(--color-border)' : 'none',
        backgroundColor: 'var(--color-bg-subtle)',
      }}>
        {/* Header */}
        <div style={s.drawerHeader}>
          <span style={s.drawerTitle}>{editingMed ? 'Edit Medication' : 'Add Medication'}</span>
          <button style={s.drawerCloseBtn} onClick={closeDrawer} aria-label="Close drawer">
            <IconX size={16} stroke={2} />
          </button>
        </div>

        {/* Body */}
        <div style={s.drawerBody} onKeyDown={onDrawerKeyDown}>
          {/* Medication */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Medication <span style={s.required}>*</span></label>

            {/* Strength-sibling dropdown when >1 sibling; else plain field
                (read-only when sourced from catalog/RxNorm). */}
            {hasSiblings ? (
              <select
                style={s.input}
                value={form.siblings.find(sib => sib.label === form.drug_name)?.key ?? ''}
                onChange={e => selectSibling(e.target.value)}
              >
                {!form.siblings.some(sib => sib.label === form.drug_name) && (
                  <option value="">{form.drug_name}</option>
                )}
                {form.siblings.map(sib => (
                  <option key={sib.key} value={sib.key}>{sib.label}</option>
                ))}
              </select>
            ) : (
              <input
                style={sourced ? { ...s.input, ...s.inputDisabled } : s.input}
                value={form.drug_name}
                onChange={e => setDoseField('drug_name', e.target.value)}
                readOnly={sourced}
                placeholder="Medication"
              />
            )}
          </div>

          {/* Strength / form — only meaningful for free-text/manual entries. */}
          <div style={s.fieldGroup}>
            <label style={s.label}>
              Strength / form
              {sourced && <span style={s.hint}> · included in medication name</span>}
            </label>
            <input
              style={sourced ? { ...s.input, ...s.inputDisabled } : s.input}
              value={form.strength_form}
              onChange={e => setDoseField('strength_form', e.target.value)}
              disabled={sourced}
              placeholder="e.g. 10 mg tablet"
            />
          </div>

          {/* Dose + Form + Route on one line */}
          <div style={s.formRow}>
            <div style={{ ...s.fieldGroup, flex: '0 0 92px' }}>
              <label style={s.label}>Dose</label>
              <ComboBox
                inputRef={doseRef}
                value={form.quantity_per_dose}
                options={DOSE_PRESETS}
                onChange={v => setDoseField('quantity_per_dose', v)}
                placeholder="e.g. 1"
              />
            </div>
            <div style={{ ...s.fieldGroup, flex: 1 }}>
              <label style={s.label}>Form / units</label>
              <select style={s.input} value={form.dose_units}
                onChange={e => setDoseField('dose_units', e.target.value)}>
                <option value="">Select...</option>
                {lists.dose_units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{ ...s.fieldGroup, flex: 1 }}>
              <label style={s.label}>Route <span style={s.hint}>(oral if blank)</span></label>
              <select style={s.input} value={form.route}
                onChange={e => setDoseField('route', e.target.value)}>
                <option value="">Oral (default)</option>
                {lists.routes.filter(r => r !== 'Oral').map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Frequency on its own line */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Frequency</label>
            <ComboBox
              value={form.frequency}
              options={lists.frequencies}
              onChange={v => setDoseField('frequency', v)}
              placeholder="Select or type (e.g. bid)"
              aliasResolver={(t) => FREQUENCY_ALIASES[normalizeFreqToken(t)] || null}
            />
          </div>

          {/* PRN */}
          <div style={s.fieldGroup}>
            <label style={s.checkboxRow}>
              <input type="checkbox" checked={form.prn}
                onChange={e => setDoseField('prn', e.target.checked)} />
              <span style={s.checkboxLabel}>As needed (PRN)</span>
            </label>
            {form.prn && (
              <input
                style={{ ...s.input, marginTop: '6px' }}
                value={form.prn_reason}
                onChange={e => setDoseField('prn_reason', e.target.value)}
                placeholder="As needed for..."
              />
            )}
          </div>

          {/* Instructions (sig) preview */}
          <div style={s.fieldGroup}>
            <label style={s.label}>
              Instructions
              {!sigOverridden && form.sig && (
                <span style={s.hint}> · auto-generated</span>
              )}
            </label>
            <textarea
              style={{ ...s.input, minHeight: '52px', resize: 'vertical' }}
              value={form.sig}
              onChange={e => { setSigOverridden(true); setField('sig', e.target.value); }}
              placeholder="Auto-generates from fields above"
            />
            {sigOverridden && (
              <button style={s.linkBtn}
                onClick={() => { setSigOverridden(false); setField('sig', generateSig(form)); }}>
                Reset to auto-generated
              </button>
            )}
          </div>

          {/* Additional information */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Additional information <span style={s.hint}>(tapers, complex regimens)</span></label>
            <textarea
              style={{ ...s.input, minHeight: '44px', resize: 'vertical' }}
              value={form.additional_info}
              onChange={e => setField('additional_info', e.target.value)}
              placeholder="Free text for anything not captured above"
            />
          </div>

          {/* Error */}
          {formError && <div style={s.errorText}>{formError}</div>}

          {/* Audit */}
          {auditLine && <div style={s.auditLine}>{auditLine}</div>}

          {/* Discontinue confirm */}
          {confirmDisc && (
            <div style={s.cautionBox}>
              <div style={s.cautionText}>Reason for discontinuing:</div>
              <select style={{ ...s.input, marginBottom: '10px' }} value={discReason}
                onChange={e => setDiscReason(e.target.value)}>
                {lists.deactivation_reasons.filter(r => r !== 'EMR integration')
                  .map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="caution" size="sm" onClick={handleDeactivate} disabled={saving}>
                  {saving ? 'Saving...' : 'Confirm discontinue'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDisc(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={s.drawerFooter}>
          {confirmDel ? (
            <div style={s.dangerBox}>
              <div style={s.dangerText}>This record will be permanently deleted.</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Confirm delete'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {editingMed && !confirmDisc && (
                  <>
                    <Button variant="caution" size="sm" onClick={() => setConfirmDisc(true)} disabled={saving}>
                      Discontinue
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setConfirmDel(true)} disabled={saving}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={closeDrawer} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // List table
  // ---------------------------------------------------------------------------
  const renderTable = (meds, isInactive) => {
    if (meds.length === 0) {
      return (
        <div style={s.emptyState}>
          {isInactive ? 'No inactive medications on file.' : 'No active medications on file.'}
        </div>
      );
    }
    return (
      <div style={s.tableWrapper}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, textAlign: 'left' }}>Medication</th>
              <th style={{ ...s.th, textAlign: 'left' }}>Directions</th>
              <th style={{ ...s.th, textAlign: 'left' }}>Additional</th>
              <th style={{ ...s.th, width: '40px' }} />
            </tr>
          </thead>
          <tbody>
            {meds.map(med => {
              const editable = isEditable(med) && !isInactive;
              const isHovered = hoveredRow === med.id;
              const isEditing = editingMed?.id === med.id;
              const medLabel = med.drug_name + (med.strength_form ? ` — ${med.strength_form}` : '');
              let directions = med.sig || '';
              if (directions.includes(', ')) {
                directions = directions.slice(directions.indexOf(', ') + 2);
              }
              return (
                <tr
                  key={med.id}
                  style={{
                    ...s.tr,
                    backgroundColor: isEditing || isHovered ? 'var(--color-accent-wash)' : 'transparent',
                    cursor: editable ? 'pointer' : 'default',
                  }}
                  onMouseEnter={() => setHoveredRow(med.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => editable && (isEditing ? closeDrawer() : openEdit(med))}
                >
                  <td style={s.tdMed}>
                    <span style={{
                      ...s.medName,
                      color: med.highlight_in_list ? 'var(--color-primary)' : 'var(--color-text-primary)',
                    }}>
                      {medLabel}
                      {med.free_text_entry && <span style={s.freeTextTag}>free text</span>}
                    </span>
                  </td>
                  <td style={s.tdDirections}>{directions || '—'}</td>
                  <td style={s.tdAdditional} title={med.additional_info || ''}>
                    {med.additional_info || '—'}
                  </td>
                  <td style={s.tdIcon}>
                    {editable && (
                      <IconPencil
                        size={15} stroke={1.5}
                        style={{
                          color: 'var(--color-text-muted)',
                          opacity: isHovered ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------
  return (
    <div style={s.root}>
      {/* Section title */}
      <div style={s.titleRow}>
        <span style={s.sectionTitle}>Medications</span>
      </div>

      {/* Controls row: tabs left, add button right */}
      <div style={s.controlsRow}>
        <div style={s.tabRow}>
          {['active', 'inactive'].map(tab => (
            <button key={tab} style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}
              onClick={() => setActiveTab(tab)}>
              {tab === 'active' ? `Active (${activeMeds.length})` : `Inactive (${inactiveMeds.length})`}
            </button>
          ))}
        </div>
        {!drawerOpen && (
          <Button variant="primary" size="sm" onClick={() => setSearchOpen(true)}>
            + Add Medication
          </Button>
        )}
      </div>

      {/* Content area */}
      <div style={s.contentArea}>
        {renderDrawer()}
        <div style={s.tablePane}>
          {loading ? (
            <div style={s.emptyState}>Loading...</div>
          ) : error ? (
            <div style={{ ...s.emptyState, color: 'var(--color-severity-high)' }}>{error}</div>
          ) : (
            renderTable(displayMeds, activeTab === 'inactive')
          )}

          {/* Anemia management orders — shown only when orders exist. */}
          {activeTab === 'active' && anemiaOrders.length > 0 && (
            <div style={s.anemiaZone}>
              <span style={s.anemiaLabel}>Anemia Management Orders</span>
              {anemiaOrders.map((o, i) => (
                <span key={i} style={s.anemiaEmpty}>{o.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer disclaimer */}
      <div style={s.footer}>
        Medications recorded here are a documentation record. Nephrōs does not perform
        dose checking — consult clinical references for CKD-specific dosing.
      </div>

      {/* Modals */}
      {searchOpen && (
        <MedicationSearchModal
          onPick={openFromPick}
          onClose={() => setSearchOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles — tokens only
// ---------------------------------------------------------------------------

const s = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-family-base)' },
  titleRow: {
    display: 'flex', alignItems: 'center', padding: '12px 16px 0 16px', flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '17px', fontWeight: 600, color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-base)',
  },
  controlsRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 16px 0 16px', gap: '8px', flexShrink: 0,
  },
  tabRow: { display: 'flex', gap: '0' },
  tab: {
    fontSize: '13px', fontWeight: 500, color: 'var(--color-text-muted)',
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '6px 14px', cursor: 'pointer', fontFamily: 'var(--font-family-base)',
    transition: 'color 0.15s ease, border-color 0.15s ease',
  },
  tabActive: { color: 'var(--color-primary)', borderBottom: '2px solid var(--color-primary)' },
  contentArea: { display: 'flex', flex: 1, overflow: 'hidden', marginTop: '8px' },

  // Drawer
  drawerHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-card)', flexShrink: 0,
  },
  drawerTitle: { fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  drawerCloseBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)',
    display: 'flex', alignItems: 'center', padding: '2px', borderRadius: '4px',
  },
  drawerBody: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  drawerFooter: {
    padding: '12px 16px', borderTop: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-card)', flexShrink: 0,
  },

  // Table
  tablePane: { flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '0 0 12px 0', minWidth: '360px' },
  tableWrapper: {
    border: '1px solid var(--color-border)', borderTop: '3px solid var(--color-accent-navy)',
    borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', margin: '0 16px', maxWidth: '1000px',
  },
  table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 'var(--font-size-sm)', fontFamily: 'var(--font-family-base)' },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
    backgroundColor: 'var(--color-subNav)', textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  tr: { borderBottom: '1px solid var(--color-border)', transition: 'background-color 0.15s ease' },
  tdMed: { padding: '10px 12px', color: 'var(--color-text-primary)', verticalAlign: 'middle' },
  tdDirections: { padding: '10px 12px', color: 'var(--color-text-muted)', verticalAlign: 'middle', fontSize: '13px' },
  tdAdditional: { padding: '10px 12px', color: 'var(--color-text-muted)', verticalAlign: 'middle', fontSize: '13px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdIcon: { padding: '10px 8px', width: '40px', textAlign: 'center', verticalAlign: 'middle' },
  medName: { fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-family-base)' },
  inputDisabled: { backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-muted)', cursor: 'not-allowed' },
  freeTextTag: {
    fontSize: '10px', fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '6px',
    backgroundColor: 'var(--color-severity-mild-bg)', padding: '1px 5px', borderRadius: '3px',
  },
  emptyState: {
    fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic', padding: '48px 24px', textAlign: 'center',
  },
  anemiaZone: { margin: '16px 16px 0 16px', padding: '12px 16px', borderTop: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: '4px' },
  anemiaLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-family-base)' },
  anemiaEmpty: { fontSize: '12px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', fontStyle: 'italic' },
  footer: {
    padding: '10px 16px', fontSize: '11px', color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-family-base)', fontStyle: 'italic',
    borderTop: '1px solid var(--color-border)', flexShrink: 0,
  },

  // Form
  formRow: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  required: { color: 'var(--color-severity-high)', marginLeft: '2px' },
  hint: { color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '11px' },
  input: {
    fontSize: '13px', padding: '7px 10px', border: '1px solid var(--color-border)',
    borderRadius: '4px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-card)', width: '100%', boxSizing: 'border-box',
  },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  checkboxLabel: { fontSize: '12px', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  errorText: { fontSize: '12px', color: 'var(--color-severity-high)', fontFamily: 'var(--font-family-base)' },
  auditLine: {
    fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic', paddingTop: '8px', borderTop: '1px solid var(--color-border)', marginTop: '4px',
  },
  linkBtn: {
    fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: 'none',
    cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font-family-base)',
    textDecoration: 'underline', alignSelf: 'flex-start',
  },
  cautionBox: { padding: '12px', borderRadius: '4px', border: '1px solid var(--color-severity-mild)', backgroundColor: 'var(--color-severity-mild-bg)' },
  cautionText: { fontSize: '13px', color: 'var(--color-text-primary)', marginBottom: '8px', fontFamily: 'var(--font-family-base)' },
  dangerBox: { padding: '12px', borderRadius: '4px', border: '1px solid var(--color-severity-high)', backgroundColor: 'var(--color-severity-high-bg)', width: '100%', boxSizing: 'border-box' },
  dangerText: { fontSize: '13px', color: 'var(--color-severity-high)', fontWeight: 600, marginBottom: '10px', fontFamily: 'var(--font-family-base)' },
};

// Search modal styles
const ms = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', width: '560px', maxWidth: '95vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 14px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 },
  title: { fontSize: '17px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex' },
  searchBar: { display: 'flex', alignItems: 'center', gap: '8px', margin: '14px 24px 0', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', backgroundColor: 'transparent' },
  searchingText: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', flexShrink: 0 },
  results: { flex: 1, overflowY: 'auto', padding: '8px 24px 16px', minHeight: '200px' },
  sectionLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 0 4px', fontFamily: 'var(--font-family-base)' },
  resultRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'background-color 0.1s ease' },
  resultName: { fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)' },
  resultSub: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', display: 'flex', alignItems: 'center', gap: '6px' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'background-color 0.1s ease' },
  nameRowOpen: { backgroundColor: 'var(--color-accent-wash)' },
  nameText: { fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)' },
  accordion: { display: 'flex', flexDirection: 'column', padding: '2px 0 6px 26px' },
  accordionHint: { fontSize: '12px', color: 'var(--color-text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)', padding: '6px 10px' },
  strengthRow: { fontSize: '13px', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)', padding: '7px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer' },
  rxDivider: { borderTop: '1px solid var(--color-border)', margin: '6px 10px' },
  hint: { fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', fontStyle: 'italic', padding: '16px 10px', textAlign: 'center' },
  footer: { display: 'flex', justifyContent: 'flex-end', padding: '12px 24px', borderTop: '1px solid var(--color-border)', flexShrink: 0 },
};